import { useEffect, useState } from 'react';
import {
  Github,
  Star,
  GitFork,
  GitBranch,
  GitPullRequest,
  CircleDot,
  Lock,
  Eye,
  Shield,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { api, ApiError } from '@/services/api';

/* ─── Types ───────────────────────────────────────────────── */

interface RepoInfo {
  owner: string;
  repo: string;
  description: string | null;
  stars: number;
  forks: number;
  defaultBranch: string;
  visibility: string;
  updatedAt: string;
  language: string | null;
  topics: string[];
  commits: { sha: string; message: string; author: string; date: string; url: string }[];
  branches: { name: string; isDefault: boolean; protected: boolean }[];
  openPullRequests: number;
  openIssues: number;
  latestWorkflowRun: {
    id: number;
    name: string;
    status: string;
    conclusion: string | null;
    url: string;
    createdAt: string;
  } | null;
}

/* ─── Helpers ─────────────────────────────────────────────── */

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
  return new Date(iso).toLocaleDateString('de-DE');
}

function conclusionColor(conclusion: string | null, status: string): string {
  if (status === 'in_progress' || status === 'queued') return 'text-yellow-400';
  if (conclusion === 'success') return 'text-green-400';
  if (conclusion === 'failure' || conclusion === 'timed_out') return 'text-red-400';
  if (conclusion === 'cancelled') return 'text-neutral-500';
  return 'text-neutral-400';
}

function ConclusionIcon({ conclusion, status }: { conclusion: string | null; status: string }) {
  const cls = `h-4 w-4 ${conclusionColor(conclusion, status)}`;
  if (status === 'in_progress' || status === 'queued') return <Clock className={cls} />;
  if (conclusion === 'success') return <CheckCircle2 className={cls} />;
  if (conclusion === 'failure' || conclusion === 'timed_out') return <XCircle className={cls} />;
  return <AlertCircle className={cls} />;
}

/* ─── Component ───────────────────────────────────────────── */

export function GitHubTab({ siteId }: { siteId: string }) {
  const [data, setData] = useState<RepoInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get<RepoInfo>(`/sites/${siteId}/github`)
      .then(setData)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotLinked(true);
        } else {
          setError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (notLinked) {
    return (
      <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
        <Github className="mb-2 h-8 w-8 text-neutral-600" />
        <p className="text-sm text-neutral-500">Kein Repository verknüpft.</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
        <Github className="mb-2 h-8 w-8 text-neutral-600" />
        <p className="text-sm text-neutral-500">Repository-Daten konnten nicht geladen werden.</p>
      </div>
    );
  }

  const repoUrl = `https://github.com/${data.owner}/${data.repo}`;

  return (
    <div className="space-y-4">
      {/* Repo Overview */}
      <div className="rounded-xl border border-surface-300 bg-surface-100 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Github className="h-5 w-5 shrink-0 text-neutral-400" />
              <a
                href={repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-lg font-semibold text-brand-400 hover:underline"
              >
                {data.owner}/{data.repo}
              </a>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  data.visibility === 'private'
                    ? 'bg-yellow-500/10 text-yellow-400'
                    : 'bg-green-500/10 text-green-400'
                }`}
              >
                {data.visibility === 'private' ? <Lock className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {data.visibility}
              </span>
            </div>
            {data.description && (
              <p className="mt-1.5 text-sm text-neutral-400">{data.description}</p>
            )}
            {data.topics.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.topics.map((t) => (
                  <span key={t} className="rounded-full bg-brand-500/10 px-2 py-0.5 text-xs text-brand-400">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-400">
          {data.language && (
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-brand-500" />
              {data.language}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5" /> {data.stars.toLocaleString('de-DE')}
          </span>
          <span className="flex items-center gap-1.5">
            <GitFork className="h-3.5 w-3.5" /> {data.forks.toLocaleString('de-DE')}
          </span>
          <span className="flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5" /> {data.defaultBranch}
          </span>
          <span className="flex items-center gap-1.5">
            <GitPullRequest className="h-3.5 w-3.5" /> {data.openPullRequests} offen
          </span>
          <span className="flex items-center gap-1.5">
            <CircleDot className="h-3.5 w-3.5" /> {data.openIssues} Issues
          </span>
          <span className="text-neutral-500">
            Aktualisiert {relativeDate(data.updatedAt)}
          </span>
        </div>
      </div>

      {/* CI/CD Status */}
      <div className="rounded-xl border border-surface-300 bg-surface-100 p-5">
        <h3 className="mb-3 text-sm font-medium text-neutral-300">CI/CD Status</h3>
        {data.latestWorkflowRun ? (
          <div className="flex items-center gap-3">
            <ConclusionIcon conclusion={data.latestWorkflowRun.conclusion} status={data.latestWorkflowRun.status} />
            <div className="min-w-0 flex-1">
              <a
                href={data.latestWorkflowRun.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-neutral-200 hover:text-brand-400 transition-colors"
              >
                {data.latestWorkflowRun.name}
                <ExternalLink className="ml-1 inline h-3 w-3" />
              </a>
              <p className="text-xs text-neutral-500">
                {data.latestWorkflowRun.conclusion ?? data.latestWorkflowRun.status}
                {' · '}
                {relativeDate(data.latestWorkflowRun.createdAt)}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-500">Keine Workflows konfiguriert.</p>
        )}
      </div>

      {/* Recent Commits */}
      {data.commits.length > 0 && (
        <div className="rounded-xl border border-surface-300 bg-surface-100">
          <h3 className="border-b border-surface-300 px-5 py-3 text-sm font-medium text-neutral-300">
            Letzte Commits
          </h3>
          <ul className="divide-y divide-surface-300">
            {data.commits.map((c) => (
              <li key={c.sha} className="flex items-start gap-3 px-5 py-2.5 hover:bg-surface-200/50 transition-colors">
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 font-mono text-xs text-brand-400 hover:underline"
                >
                  {c.sha.slice(0, 7)}
                </a>
                <span className="min-w-0 flex-1 truncate text-sm text-neutral-300">
                  {c.message.split('\n')[0]}
                </span>
                <span className="shrink-0 whitespace-nowrap text-xs text-neutral-500">
                  {c.author} · {relativeDate(c.date)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Branches */}
      {data.branches.length > 0 && (
        <div className="rounded-xl border border-surface-300 bg-surface-100">
          <h3 className="border-b border-surface-300 px-5 py-3 text-sm font-medium text-neutral-300">
            Branches ({data.branches.length})
          </h3>
          <div className="flex flex-wrap gap-2 p-5">
            {data.branches.map((b) => (
              <span
                key={b.name}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-mono ${
                  b.isDefault
                    ? 'border-brand-500/30 bg-brand-500/10 text-brand-400'
                    : 'border-surface-300 text-neutral-400'
                }`}
              >
                <GitBranch className="h-3 w-3" />
                {b.name}
                {b.protected && <Shield className="h-3 w-3 text-yellow-400" />}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
